import { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { debounce, isEmpty } from "lodash";
import { Button, Checkbox, Dialog, FormGroup, H2, MenuItem } from "@blueprintjs/core";
import { Select } from "@blueprintjs/select";

import { Analytics } from "utils/analytics";
import { validate, SingleInputType } from "utils/form";
import { selectCurrentDialogue, setCurrentDialogue } from "controllers/common/action";
import { selectCreateUserError, selectCreateUserRequest, createUser, selectCreateUser } from "controllers/users/action";
import Categories from "constants/Categories";

import SingleFormInput from "components/SingleFormInput";
import VerifyEmail from "./VerifyEmail";
import "./SignUp.scss";

interface SignUpProps {
  isOpen?: boolean;
  onClose?: () => void;
}

export const StringSelect = Select.ofType<string>();

const categories = Categories.values().map((v) => v.label);

export default function SignUp(props: SignUpProps) {
  const { isOpen, onClose } = props;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentDialogue = useSelector(selectCurrentDialogue);
  const shouldOpen = isOpen || currentDialogue === "SignUp";

  const error = useSelector(selectCreateUserError);
  const success = useSelector(selectCreateUser);
  const request = useSelector(selectCreateUserRequest);

  const [formInfo, setFormInfo] = useState({
    firstName: "",
    lastName: "",
    institution: "",
    institutionCategory: "",
    otherCategory: "",
    email: "",
    password: "",
    agreePolicy: false,
  });
  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    institution: "",
    institutionCategory: "",
    email: "",
    password: "",
  });
  const [showPW, setShowPW] = useState(false);

  useEffect(() => {
    if (shouldOpen) {
      Analytics.getInstance().event("Sign Up", "Opened");
    }
    return () => {};
  }, [shouldOpen]);

  useEffect(() => {
    if (!isEmpty(success) && formInfo.email === request?.email) {
      dispatch(setCurrentDialogue("VerifyEmail"));
      setFormInfo((state) => ({
        firstName: "",
        lastName: "",
        institution: "",
        institutionCategory: "",
        otherCategory: "",
        email: state.email,
        password: "",
        agreePolicy: false,
      }));
      setErrors({
        firstName: "",
        lastName: "",
        institution: "",
        institutionCategory: "",
        email: "",
        password: "",
      });
      onClose?.();
    }
  }, [success, formInfo.email, request, dispatch, onClose]);

  const handleChange = (event: any) => {
    event.preventDefault();
    const { name, value } = event.target;
    setFormInfo((state) => ({ ...state, [name]: value }));
    debounceField({ name, value });
  };
  const handleCheckbox = (event: any) => {
    const { name, checked } = event.target;
    setFormInfo((state) => ({ ...state, [name]: checked }));
  };

  const handleLogin = () => {
    dispatch(setCurrentDialogue("Login"));
    onClose?.();
  };

  const shouldClose = () => {
    dispatch(setCurrentDialogue(""));
    onClose?.();
  };

  const handleSubmit = (event: any) => {
    event.preventDefault();
    const payload = formInfo;
    const emptyKeys = Object.keys(payload).filter((key) => isEmpty(payload[key as keyof typeof payload]));
    emptyKeys.forEach((key) => {
      if (key !== "otherCategory" && key !== "agreePolicy") {
        setErrors((state) => ({ ...state, [key as keyof typeof errors]: `Please provide required input` }));
      }
    });

    if (Object.keys(errors).every((key) => !errors[key as keyof typeof errors]) && formInfo.agreePolicy) {
      dispatch(createUser(payload));
    }
  };
  const handleShowPW = () => setShowPW((state) => !state);

  const debounceField = debounce(({ name, value }) => {
    let validName = name;
    switch (name) {
      case "email":
        validName = name;
        break;
      default:
        validName = "text";
    }
    const { isValid, getMessage } = validate(validName);
    if (!isValid(value, [])) {
      setErrors((state) => ({ ...state, [name]: getMessage(value, []) }));
    } else {
      setErrors((state) => ({ ...state, [name]: "" }));
    }
  }, Number(process.env.REACT_APP_DEBOUNCE));

  const signUpForm: SingleInputType[] = [
    {
      name: "firstName",
      label: "First Name",
      labelInfo: "*",
      value: formInfo.firstName,
    },
    {
      name: "lastName",
      label: "Last name",
      labelInfo: "*",
      value: formInfo.lastName,
    },
    {
      name: "institution",
      label: "Institution",
      labelInfo: "*",
      value: formInfo.institution,
    },
    {
      name: "otherCategory",
      label: "Other Category",
      value: formInfo.otherCategory,
    },
    {
      name: "email",
      type: "email",
      label: "Email",
      labelInfo: "*",
      value: formInfo.email,
    },
    {
      name: "password",
      type: showPW ? "text" : "password",
      label: "Password",
      labelInfo: "*",
      helperText: "Password must be at least 8 characters",
      value: formInfo.password,
    },
  ];
  const handleRedirect = () => navigate("/");
  return (
    <Fragment>
      <Dialog className="signup" isOpen={shouldOpen}>
        <div className="form__header">
          <H2>Create Account</H2>
          <Button minimal intent="primary" icon="cross" onClick={onClose ? shouldClose : handleRedirect} />
        </div>
        {error ? <p className="red helperText">{error.error}</p> : ""}
        <form onSubmit={handleSubmit}>
          <p className="lighterText">*Required information</p>
          <div className="form--oneline">
            {signUpForm.slice(0, 2).map((input) => {
              input.handleChange = handleChange;
              input.error = errors[input.name as keyof typeof errors];
              return <SingleFormInput {...input} key={input.name} />;
            })}
          </div>
          <SingleFormInput
            {...signUpForm[2]}
            handleChange={handleChange}
            error={errors[signUpForm[2].name as keyof typeof errors]}
            key={signUpForm[2].name}
          />

          <div className="form--oneline">
            <FormGroup label={"Institution Category"} labelInfo="*" className="single-input">
              <StringSelect
                items={categories}
                filterable={false}
                itemRenderer={(item, { handleClick, modifiers }) => {
                  return (
                    <MenuItem
                      active={modifiers.active}
                      disabled={modifiers.disabled}
                      key={item}
                      text={item}
                      onClick={handleClick}
                    />
                  );
                }}
                onItemSelect={(item) => {
                  setFormInfo((state) => ({ ...state, institutionCategory: item }));
                  setErrors((state) => ({ ...state, institutionCategory: "" }));
                }}
                popoverProps={{
                  minimal: true,
                  popoverClassName: "select-popover",
                }}
              >
                <Button
                  className="button-category"
                  text={formInfo.institutionCategory || "Please Select Category"}
                  rightIcon="double-caret-vertical"
                />
              </StringSelect>
              {errors.institutionCategory ? <span className="red helperText">{errors.institutionCategory}</span> : ""}
            </FormGroup>

            {formInfo.institutionCategory === "Other" && (
              <SingleFormInput
                {...signUpForm[3]}
                handleChange={handleChange}
                error={errors[signUpForm[2].name as keyof typeof errors]}
                key={signUpForm[3].name}
              />
            )}
          </div>
          {signUpForm.slice(4).map((input) => {
            input.handleChange = handleChange;
            input.error = errors[input.name as keyof typeof errors];
            return (
              <SingleFormInput
                className={input.name === "password" ? "password" : undefined}
                {...input}
                key={input.name}
              />
            );
          })}
          <Checkbox checked={showPW} onChange={handleShowPW}>
            {"Show Password"}
          </Checkbox>
          <Checkbox
            className="signup__agreePolicy"
            name="agreePolicy"
            checked={formInfo.agreePolicy}
            onChange={handleCheckbox}
          >
            By creating an account, I agree with PNNL's
            <a href={`${process.env.REACT_APP_TERMS_OF_SERVICE}`} target="_blank" rel="noreferrer">
              {" "}
              security and privacy policy
            </a>
            .
          </Checkbox>

          <div className="form__footer">
            <Button
              className="button-left"
              minimal
              intent="primary"
              key="login"
              icon="log-in"
              text="Log In Instead"
              onClick={handleLogin}
            />
            <Button
              className="button-submit"
              intent="primary"
              key="signup"
              type="submit"
              icon="new-person"
              text="Create Account"
              disabled={
                !formInfo.agreePolicy ||
                !Object.keys(errors).every((key) => isEmpty(errors[key as keyof typeof errors]))
              }
            />
          </div>
        </form>
      </Dialog>
      <VerifyEmail email={formInfo.email} />
    </Fragment>
  );
}
